import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { RiskLevel } from './riskTypes';

interface RiskBadgeProps {
  riskLevel: RiskLevel;
  riskFactors: string[];
  suppressReminders: boolean;
}

const RISK_BADGE: Record<RiskLevel, { bg: string; border: string; label: string; solid?: boolean }> = {
  low:      { bg: '#f0fdf4', border: '#22c55e', label: 'Low risk' },
  moderate: { bg: '#fffbeb', border: '#f59e0b', label: 'Moderate risk' },
  high:     { bg: '#fef2f2', border: '#ef4444', label: 'High risk' },
  critical: { bg: '#ef4444', border: '#dc2626', label: 'Critical', solid: true },
};

export function RiskBadge({ riskLevel, riskFactors, suppressReminders }: RiskBadgeProps) {
  const [tipVisible, setTipVisible] = useState(false);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!tipVisible) return;
    const handler = () => setTipVisible(false);
    document.addEventListener('scroll', handler, true);
    return () => document.removeEventListener('scroll', handler, true);
  }, [tipVisible]);

  if (suppressReminders) {
    return (
      <span
        title="Reminders suppressed"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          borderRadius: 3,
          background: 'rgba(0,0,0,0.08)',
          color: '#94a3b8',
          fontSize: 9,
          flexShrink: 0,
        }}
      >
        ∅
      </span>
    );
  }

  // low risk: only visible on hover (handled by parent via CSS class, but we still render)
  if (riskLevel === 'low') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '1px 3px',
          borderRadius: 3,
          fontSize: 8,
          fontWeight: 600,
          lineHeight: 1,
          border: '1px solid #22c55e',
          background: '#f0fdf4',
          color: '#15803d',
          opacity: 0,
          transition: 'opacity 0.15s',
          flexShrink: 0,
        }}
        className="fse-risk-badge-low"
        title={riskFactors.join(' · ') || 'Low risk'}
      >
        ●
      </span>
    );
  }

  const cfg = RISK_BADGE[riskLevel];

  return (
    <>
      <span
        ref={ref}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: cfg.solid ? '1px 4px' : '1px 4px',
          borderRadius: 3,
          fontSize: 8,
          fontWeight: 700,
          lineHeight: 1,
          background: cfg.bg,
          border: `1px solid ${cfg.border}`,
          color: cfg.solid ? '#fff' : cfg.border,
          flexShrink: 0,
          cursor: 'default',
          userSelect: 'none',
        }}
        onMouseEnter={(e) => {
          setTipPos({ x: e.clientX, y: e.clientY });
          setTipVisible(true);
        }}
        onMouseLeave={() => setTipVisible(false)}
      >
        {riskLevel === 'critical' ? '⚠ CRIT' : riskLevel === 'high' ? '⚠ HIGH' : 'MOD'}
      </span>

      {tipVisible && riskFactors.length > 0 && createPortal(
        <div
          style={{
            position: 'fixed',
            left: tipPos.x + 12,
            top: tipPos.y + 12,
            background: '#1f2937',
            color: '#f9fafb',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 11,
            lineHeight: 1.5,
            maxWidth: 260,
            zIndex: 99999,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 3, color: cfg.solid ? '#fca5a5' : '#fbbf24' }}>
            {cfg.label}
          </div>
          {riskFactors.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 5, alignItems: 'flex-start' }}>
              <span style={{ color: '#9ca3af', flexShrink: 0 }}>·</span>
              <span>{f}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
